import { CommonModule } from "@angular/common";
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  signal,
  untracked,
  viewChild,
} from "@angular/core";
import Chart from "chart.js/auto";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { Widget } from "../../../dashboard/models/dashboard.model";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";

// UI
import { SharedModule } from "../../../../shared/shared.module";
import { PopoverModule } from "../../../../shared/modules/popover/popover.module";
import { ensureChartJs } from "../../../articles/services/chart.utils";
import { groupBy, toDayKey } from "../../../articles/services/stats.util";
import { ArticlesInfosFacadeService } from "../../../articles/services/article-infos/article-infos-facade.service";
import { SelectAutocompleteComponent } from "../../../../shared/modules/select-autocomplete/select-autocomplete.component";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { ChipsToggleComponent } from "../../../../shared/modules/chips-toggle/chips-toggle.component";
import { TiersFacadeService } from "../../services/tiers/tiers-facade.service";
import { FactureLigne } from "../../../facture/models/facture.model";
import { StatsTiers } from "../../models/stats-tiers.model";
import { parseNumber } from "../../../../shared/utils/number-utils.service";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";
import { LocalStorageService } from "../../../../shared/services/local-storage.service";
import {
  StatsVenteEvolutionDialogComponent,
  VenteEvolutionDialogData,
  VenteEvolutionFactureRow,
  VenteEvolutionLot,
} from "./stats-vente-evolution-dialog.component";

type OrderPoint = {
  key: string; // id/clé de facture
  numero: string; // numéro de facture lisible
  dateKey: string; // YYYY-MM-DD (date facture)
  label: string; // "JJ/MM • NUMÉRO"

  // Unités **par UV** (unité de vente)
  pu: number; // PU vente moyen pondéré (par UV)
  costPU: number; // PU coût moyen pondéré (par UV)
  marginPU: number; // Marge unitaire = pu - costPU
  marginPct: number; // Marge % = marginPU / pu
  qty: number; // Quantité facturée (UV) avec signe (avoirs)

  // Totaux par facture (calculés pour le graphe à 2 colonnes)
  totalCA: number;
  totalCost: number;
  totalMargin: number;
};

@Component({
  selector: "vl-stats-vente-evolution-tiers",
  standalone: true,
  imports: [
    CommonModule,
    WidgetTitleComponent,
    SharedModule,
    PopoverModule,
    SelectAutocompleteComponent,
    ChipsComponent,
    ChipsToggleComponent,
  ],
  templateUrl: "./stats-article-vente-evolution-tiers.component.html",
  styleUrls: ["./stats-article-vente-evolution-tiers.component.scss"],
  host: { class: "widget-component" },
})
export class StatsVenteEvolutionByArticleComponent implements OnDestroy {
  private readonly tiersFacade = inject(TiersFacadeService);
  stats = this.tiersFacade.getState().stats;

  metadata = input<EntityMetadata>();
  widget = input<Widget>();

  /** Optionnel : map externe des coûts unitaires par lotId (prioritaire si fourni) */
  lotUnitCostById = input<Record<string, number>>({});

  articleInfosFacade = inject(ArticlesInfosFacadeService);
  private dialogsService = inject(DialogsService);
  private localStorage = inject(LocalStorageService);

  private readonly STORAGE_KEY = "justup-widget-vente-evolution";

  // Canvas en signal
  canvasRef = viewChild<ElementRef<HTMLCanvasElement>>("articleVente");
  private chart?: Chart;

  chartMode = signal<"pu" | "ca">("ca");
  chartModeElements = [
    { name: "PU", value: "pu" as const },
    { name: "CA", value: "ca" as const },
  ];

  // ===== Sélection Article =====
  selectedArticleId = signal<string>("");

  // Articles vendus : extraits des factures (pas de articlesFournis côté client)
  articlesOptions = computed<{ name: string; value: string }[]>(() => {
    const lines = this.stats()?.facturesLignes ?? [];
    const map = new Map<string, string>();
    for (const l of lines as any[]) {
      const articleId: string =
        l?.commandeClientLigne?.articleId ??
        l?.commandeClientLigne?.article?.id ??
        l?.articleId ?? "";
      if (!articleId || map.has(articleId)) continue;
      const name = this.articleInfosFacade.getByArticleId(articleId)?.()?.denomination ?? articleId;
      map.set(articleId, name);
    }
    return [...map.entries()]
      .map(([value, name]) => ({ value, name }))
      .sort((a, b) => a.name.localeCompare(b.name, "fr"));
  });

  /** Lorsque la liste change, on initialise/assainit la sélection */
  constructor() {
    // Restore persisted article (si toujours disponible) puis fallback sur premier.
    effect(
      () => {
        const opts = this.articlesOptions();
        untracked(() => {
          const sel = this.selectedArticleId();
          if (opts.find((o) => o.value === sel)) return;
          const saved = this.localStorage.getWithTTL(`${this.STORAGE_KEY}-article`) as string;
          const pick =
            (saved && opts.find((o) => o.value === saved)?.value) ||
            opts[0]?.value || "";
          if (pick !== sel) this.selectedArticleId.set(pick);
        });
      },
      { allowSignalWrites: true },
    );

    // Aligne le TopN sur le max disponible quand stats/article change
    effect(
      () => {
        this.stats();
        this.selectedArticleId();
        const max = this.maxRows();
        this.topCount.set(max || 1);
      },
      { allowSignalWrites: true },
    );

    // Persiste l'article sélectionné
    effect(() => {
      const id = this.selectedArticleId();
      if (id) this.localStorage.putWithTTL(`${this.STORAGE_KEY}-article`, id);
    });

    // Build/destroy chart selon DOM + data + mode
    effect(
      () => {
        ensureChartJs();
        this.chartMode();
        if (this.canvasRef() && this.pointsTop().length) {
          this.buildChart();
        } else {
          this.destroyChart();
        }
      },
      { allowSignalWrites: true },
    );

    // Mise à jour live du chart
    effect(() => this.updateChart(), { allowSignalWrites: true });

    // Clamp du slider si le max change
    effect(
      () => {
        const max = this.maxRows();
        const cur = this.topCount();
        const clamped = Math.min(Math.max(1, cur), Math.max(1, max));
        if (clamped !== cur) this.topCount.set(clamped);
      },
      { allowSignalWrites: true },
    );
  }

  // ===== Filtres =====
  topCount = signal<number>(20); // N dernières factures
  search = signal<string>(""); // prêt si besoin

  // ===== Données brutes => points par facture (filtrés sur l’article) =====
  private resolveArticleId(l: any): string {
    return l?.commandeClientLigne?.articleId ??
      l?.commandeClientLigne?.article?.id ??
      l?.articleId ?? "";
  }

  private filteredFactureLignes = computed<FactureLigne[]>(() => {
    const sel = this.selectedArticleId();
    if (!sel) return [];
    const lines = this.stats()?.facturesLignes ?? [];
    return lines.filter((l) => this.resolveArticleId(l) === sel);
  });

  /**
   * Résolution coût unitaire (UG) d'un lot.
   *
   * Priorité :
   * 1. Map externe `lotUnitCostById` (override)
   * 2. `stats().lotCosts` — source de vérité coûts profonds (couvre lots produits ET reçus,
   *    inclut les coûts saisis manuellement via le suivi des coûts)
   * 3. Lot trouvé dans `of.lots` avec `coutMaterielUnitaire + coutPersonnelUnitaire` renseignés
   * 4. Fallback historique : moyenne OF (`coutMatiereTotalReel + coutPersonnelTotalReel) / quantiteProduite`)
   */
  private resolveLotUnitCost(lotId: string): number {
    if (!lotId) return 0;

    // 1) Map externe
    const map = this.lotUnitCostById() || {};
    if (map[lotId] != null) return parseNumber(map[lotId]) || 0;

    const s: StatsTiers = this.stats?.();

    // 2) lotCosts — source de vérité coûts profonds
    const deep = s?.lotCosts?.find((c) => c.lotId === lotId);
    if (deep) {
      return (parseNumber(deep.coutMat) || 0) + (parseNumber(deep.coutMO) || 0);
    }

    // 3) Lot dans OF avec coûts unitaires renseignés
    for (const fo of s?.ofs ?? []) {
      const lot: any = fo.lots?.find?.((l: any) => l.id === lotId);
      if (!lot) continue;
      const mat = parseNumber(lot.coutMaterielUnitaire);
      const mo = parseNumber(lot.coutPersonnelUnitaire);
      if ((mat || 0) + (mo || 0) > 0) return (mat || 0) + (mo || 0);

      // 4) Fallback : moyenne OF
      const qte = parseNumber(fo.quantiteProduite) || 1;
      const total = (parseNumber(fo.coutMatiereTotalReel) || 0) + (parseNumber(fo.coutPersonnelTotalReel) || 0);
      return total / qte;
    }

    return 0;
  }

  private pointsAll = computed<OrderPoint[]>(() => {
    const rows = this.filteredFactureLignes();
    if (!rows.length) return [];

    const byFacture = groupBy(rows, (r: any) => r?.factureId ?? r?.facture?.id);

    const fmtFR = new Intl.DateTimeFormat("fr-FR", {
      day: "2-digit",
      month: "2-digit",
    });

    const list: OrderPoint[] = Object.keys(byFacture).map((key) => {
      const arr = byFacture[key] ?? [];

      // Agrégation en **unité de vente** (UV)
      let qtyUVTotal = 0; // somme des quantités (UV), signe inclus (avoirs)
      let revenueTotal = 0; // somme(qtyUV * puVenteUV)
      let costTotal = 0; // somme(qtyUV * puCoutUV)

      for (const r of arr) {
        const coefUG =
          parseNumber(r?.commandeClientLigne?.coefficientUniteGestion ?? 1) ||
          1;

        const qtyUV = parseNumber(r?.quantite ?? 0); // quantité en UV
        const puVenteUV = parseNumber(r?.prix ?? 0); // prix unitaire UV
        const puCoutUG = this.resolveLotUnitCost(r?.lotId) || 0; // coût / UG
        const puCoutUV = puCoutUG * coefUG; // coût ramené en UV

        qtyUVTotal += qtyUV;
        revenueTotal += qtyUV * puVenteUV;
        costTotal += qtyUV * puCoutUV;
      }

      // Date = date facture (fallback première ligne)
      const dates = arr.map((r: any) => toDayKey(r?.facture?.date));
      const dateKey = dates.sort().at(-1) ?? toDayKey(arr[0]?.facture?.date);

      // Numéro lisible (fallback clé)
      const numero = arr?.[0]?.facture?.numero ?? String(key);

      // PU moyens **par UV**
      const puVente = qtyUVTotal !== 0 ? revenueTotal / qtyUVTotal : 0;
      const puCout = qtyUVTotal !== 0 ? costTotal / qtyUVTotal : 0;

      const marginPU = puVente - puCout; // marge unitaire UV
      const marginPct = puVente > 0 ? marginPU / puVente : 0; // % marge

      // Totaux (pour graph « 2 colonnes »)
      const totalCA = revenueTotal;
      const totalCost = costTotal;
      const totalMargin = totalCA - totalCost;

      // Label "JJ/MM • NUMÉRO"
      const labelDate = (() => {
        try {
          return fmtFR.format(new Date(dateKey));
        } catch {
          return dateKey;
        }
      })();
      const label = `${labelDate} • ${numero}`;

      return {
        key,
        numero,
        dateKey,
        label,
        pu: puVente,
        costPU: puCout,
        marginPU,
        marginPct,
        qty: qtyUVTotal,
        totalCA,
        totalCost,
        totalMargin,
      };
    });

    // Tri chronologique (ASC), puis numéro pour stabilité
    return list.sort((a, b) =>
      a.dateKey === b.dateKey
        ? a.numero.localeCompare(b.numero)
        : a.dateKey.localeCompare(b.dateKey),
    );
  });

  // Recherche, puis Top N des + récentes
  private pointsFiltered = computed<OrderPoint[]>(() => {
    const q = this.search().trim().toLowerCase();
    if (!q) return this.pointsAll();
    return this.pointsAll().filter((p) => p.numero.toLowerCase().includes(q));
  });
  maxRows = computed(() => this.pointsFiltered().length);

  pointsTop = computed<OrderPoint[]>(() => {
    const n = Math.max(1, Math.min(this.topCount(), this.maxRows()));
    const arr = this.pointsFiltered();
    return arr.slice(Math.max(0, arr.length - n)); // N dernières, en ordre chrono
  });

  /** Libellé résumé sous le titre : PU moyen pondéré + marge moyenne. */
  summaryLabel = computed<string | null>(() => {
    const points = this.pointsAll();
    if (!points.length) return null;
    let qty = 0, ca = 0, cost = 0;
    for (const p of points) {
      qty += p.qty;
      ca += p.totalCA;
      cost += p.totalCost;
    }
    if (qty <= 0 || ca <= 0) return null;
    const pu = ca / qty;
    const taux = ((ca - cost) / ca) * 100;
    return `PU moy. ${pu.toFixed(2)} € · Marge ${taux.toFixed(0)}%`;
  });

  selectedArticleDenomination = computed<string>(() => {
    const id = this.selectedArticleId();
    if (!id) return "";
    return this.articleInfosFacade.getByArticleId(id)?.()?.denomination ?? id;
  });

  /** Labels pour l'axe X */
  private labels = computed<string[]>(() =>
    this.pointsTop().map((p) => p.label),
  );

  // ====== NOUVEAU: 2 colonnes par facture ======
  // Colonne 1 : CA total (barre simple)
  private dsCA = computed(() => {
    const data = this.pointsTop().map((p) => p.totalCA);
    return {
      type: "bar" as const,
      label: "CA",
      data,
      stack: "A", // pile indépendante pour la « barre simple »
      maxBarThickness: 28,
      order: 1,
    };
  });

  // Colonne 2 : Coût + Marge (barres empilées)
  private dsCost = computed(() => {
    const data = this.pointsTop().map((p) => p.totalCost);
    return {
      type: "bar" as const,
      label: "Coût",
      data,
      stack: "B", // même stack que la marge
      maxBarThickness: 28,
      order: 2, // s'affiche avant Marge dans la pile
    };
  });

  private dsMargin = computed(() => {
    const data = this.pointsTop().map((p) => p.totalMargin);
    return {
      type: "bar" as const,
      label: "Marge",
      data,
      stack: "B",
      maxBarThickness: 28,
      order: 3,
    };
  });

  private dsPUBar = computed(() => ({
    type: "bar" as const,
    label: "PU vente",
    data: this.pointsTop().map((p) => p.pu),
    maxBarThickness: 36,
    order: 2,
  }));

  private dsPUAvg = computed(() => {
    const pts = this.pointsTop();
    let accPV = 0, accQ = 0;
    return {
      type: "line" as const,
      label: "Moyenne pondérée cumulée",
      data: pts.map((p) => {
        accPV += (p.pu ?? 0) * (p.qty ?? 0);
        accQ += p.qty ?? 0;
        return accQ > 0 ? accPV / accQ : 0;
      }),
      pointRadius: 2,
      pointHoverRadius: 4,
      tension: 0.2,
      fill: false,
      order: 1,
    };
  });

  openFullList() {
    const lines = this.filteredFactureLignes();
    if (!lines.length) return;

    const articleInfo = this.articleInfosFacade.getByArticleId(this.selectedArticleId())?.();
    const uniteVente = (articleInfo?.unite as any)?.denomination || "UV";
    const uniteGestion = uniteVente;
    const tiersDenomination = this.stats()?.tiers?.denomination ?? "";

    // Regroupement par factureId puis par lotId
    const byFacture = groupBy(lines, (r: any) => r?.factureId ?? r?.facture?.id);

    const factures: VenteEvolutionFactureRow[] = Object.keys(byFacture).map((key) => {
      const arr = byFacture[key] ?? [];
      const first: any = arr[0] ?? {};
      const numero = first?.facture?.numero ?? String(key);
      const dateKey =
        arr
          .map((r: any) => toDayKey(r?.facture?.date))
          .filter((d: string) => !!d)
          .sort()
          .at(-1) ?? "";
      const clientDenomination = first?.facture?.tiers?.denomination ?? "";

      // Agrégats facture (UV)
      let qtyUV = 0, caTotal = 0, coutTotal = 0;

      // Breakdown par lot
      const byLot = new Map<string, {
        qtyUV: number; qtyUG: number;
        ca: number; cost: number;
        puVenteSum: number; puCoutSum: number;
        puCount: number;
        uniteVente: string; uniteGestion: string;
      }>();

      for (const r of arr as any[]) {
        const coefUG = parseNumber(r?.commandeClientLigne?.coefficientUniteGestion ?? 1) || 1;
        const qUV = parseNumber(r?.quantite ?? 0);
        const puV = parseNumber(r?.prix ?? 0);
        const puCoutUG = this.resolveLotUnitCost(r?.lotId) || 0;
        const puCoutUV = puCoutUG * coefUG;

        qtyUV += qUV;
        caTotal += qUV * puV;
        coutTotal += qUV * puCoutUV;

        const lotKey = r?.lotId || "__unknown__";
        if (!byLot.has(lotKey)) {
          byLot.set(lotKey, {
            qtyUV: 0, qtyUG: 0, ca: 0, cost: 0,
            puVenteSum: 0, puCoutSum: 0, puCount: 0,
            uniteVente,
            uniteGestion,
          });
        }
        const l = byLot.get(lotKey)!;
        l.qtyUV += qUV;
        l.qtyUG += qUV / (coefUG || 1);
        l.ca += qUV * puV;
        l.cost += qUV * puCoutUV;
        if (qUV > 0) {
          l.puVenteSum += puV;
          l.puCoutSum += puCoutUV;
          l.puCount++;
        }
      }

      const lots: VenteEvolutionLot[] = [...byLot.entries()].map(([lotKey, v]) => {
        const marge = v.ca - v.cost;
        const tauxMarge = v.ca > 0 ? (marge / v.ca) * 100 : 0;
        const puVente = v.puCount ? v.puVenteSum / v.puCount : 0;
        const puCout = v.puCount ? v.puCoutSum / v.puCount : 0;
        const lotNumero =
          (arr.find((r: any) => (r?.lotId || "__unknown__") === lotKey) as any)?.lot?.numero ??
          (lotKey === "__unknown__" ? "" : lotKey);
        const lotId = lotKey === "__unknown__" ? null : lotKey;
        return {
          lotId,
          lotNumero,
          qtyUV: v.qtyUV,
          qtyUG: v.qtyUG,
          uniteVente: v.uniteVente,
          uniteGestion: v.uniteGestion,
          puVente,
          puCout,
          marge,
          tauxMarge,
        };
      }).sort((a, b) => b.qtyUV - a.qtyUV);

      const marge = caTotal - coutTotal;
      const tauxMarge = caTotal > 0 ? (marge / caTotal) * 100 : 0;
      const puVenteMoyen = qtyUV !== 0 ? caTotal / qtyUV : 0;
      const puCoutMoyen = qtyUV !== 0 ? coutTotal / qtyUV : 0;

      return {
        factureId: String(key),
        numero,
        dateKey,
        clientDenomination,
        qtyUV,
        puVenteMoyen,
        puCoutMoyen,
        ca: caTotal,
        cout: coutTotal,
        marge,
        tauxMarge,
        lots,
      };
    });

    const data: VenteEvolutionDialogData = {
      articleDenomination: this.selectedArticleDenomination(),
      articleUniteVente: uniteVente,
      tiersDenomination,
      tiersId: this.stats()?.tiers?.id,
      articleId: this.selectedArticleId() || undefined,
      factures,
    };

    this.dialogsService.open(
      StatsVenteEvolutionDialogComponent,
      "min(95vw, 860px)",
      "fit-content",
      data,
    );
  }

  ngOnDestroy(): void {
    this.destroyChart();
  }

  // ===== Chart =====
  private buildChart(): void {
    const canvas = this.canvasRef();
    const ctx = canvas?.nativeElement.getContext("2d");
    if (!ctx) return;

    const css = getComputedStyle(canvas!.nativeElement);
    const BG_ALT = (css.getPropertyValue("--color-on-background-alt") || "#476373").trim();
    const WARN = (css.getPropertyValue("--color-warn") || "#9db8c3").trim();
    const TERTIARY = (css.getPropertyValue("--color-tertiary") || "#c535ff").trim();
    const ACCENT = (css.getPropertyValue("--color-accent") || "#c535ff").trim();
    const ON_ACCENT = (css.getPropertyValue("--color-on-accent") || "#ffffff").trim();
    const ON_ACCENT_ALT = (css.getPropertyValue("--color-on-accent-alt") || "rgba(197,53,255,.35)").trim();
    const OUTLINE = (css.getPropertyValue("--color-outline") || "#9db8c3").trim();

    this.destroyChart?.();

    const mode = this.chartMode();
    let datasets: any[];
    let yStacked: boolean;
    let tooltipCallbacks: any;

    if (mode === "pu") {
      const bar = { ...(this.dsPUBar() as any) };
      bar.backgroundColor = ON_ACCENT_ALT;
      bar.borderColor = "transparent";
      bar.borderWidth = 0;

      const line = { ...(this.dsPUAvg() as any) };
      line.borderColor = ACCENT;
      line.backgroundColor = "rgba(0,0,0,0)";
      line.borderWidth = 5;
      line.pointBackgroundColor = ACCENT;
      line.pointBorderColor = ON_ACCENT;
      line.pointBorderWidth = 3;
      line.pointRadius = 7;

      datasets = [bar, line];
      yStacked = false;
      tooltipCallbacks = {
        title: (items: any[]) => items?.[0]?.label ?? "",
        label: (ctx: any) => {
          const idx = ctx.dataIndex ?? 0;
          const p = (this as any).pointsTop?.()[idx] as OrderPoint | undefined;
          const eur = (v: number) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 4 }).format(v ?? 0);
          if (ctx.dataset.type === "bar") {
            return `PU vente: ${eur(ctx.parsed.y)}  •  Qté: ${p?.qty ?? 0}`;
          }
          return `Moyenne pondérée cumulée: ${eur(ctx.parsed.y)}`;
        },
      };
    } else {
      const ca = { ...(this.dsCA() as any) };
      ca.backgroundColor = ON_ACCENT_ALT;
      ca.borderColor = "transparent";

      const cost = { ...(this.dsCost() as any) };
      cost.backgroundColor = WARN;
      cost.borderColor = "transparent";

      const margin = { ...(this.dsMargin() as any) };
      margin.backgroundColor = TERTIARY;
      margin.borderColor = "transparent";

      datasets = [ca, cost, margin];
      yStacked = true;
      tooltipCallbacks = {
        title: (items: any[]) => items?.[0]?.label ?? "",
        label: (ctx: any) => {
          const idx = ctx.dataIndex ?? 0;
          const p = (this as any).pointsTop?.()[idx] as OrderPoint | undefined;
          const eur = (v: number, max = 2) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: max }).format(v ?? 0);
          const pct = (v: number) => new Intl.NumberFormat("fr-FR", { style: "percent", maximumFractionDigits: 1 }).format(v ?? 0);
          const dsLabel = ctx.dataset?.label || "";
          const val = parseNumber(ctx.parsed?.y ?? 0);
          if (dsLabel === "CA") return ` CA: ${eur(val)}`;
          if (dsLabel === "Coût") return ` Coût: ${eur(val, 2)}`;
          if (dsLabel === "Marge") return ` Marge: ${eur(val, 2)}${p ? ` (${pct(p.marginPct)})` : ""}`;
          return `${dsLabel}: ${eur(val)}`;
        },
        footer: (items: any[]) => {
          if (!items?.length) return "";
          const p = (this as any).pointsTop?.()[items[0].dataIndex ?? 0] as OrderPoint | undefined;
          if (!p) return "";
          const eur = (v: number) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(v ?? 0);
          return ` CA = Coût + Marge → ${eur(p.totalCA)} = ${eur(p.totalCost)} + ${eur(p.totalMargin)}`;
        },
      };
    }

    this.chart = new Chart(ctx, {
      type: "bar",
      data: { labels: this.labels(), datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: { callbacks: tooltipCallbacks },
        },
        scales: {
          y: {
            stacked: yStacked,
            grid: { color: mode === "pu" ? OUTLINE : WARN },
            border: { color: mode === "pu" ? OUTLINE : WARN },
            ticks: {
              color: BG_ALT,
              callback: (v) =>
                new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: mode === "pu" ? 2 : 0 }).format(parseNumber(v)),
            },
          },
          x: {
            stacked: false,
            grid: { display: false },
            border: { color: mode === "pu" ? OUTLINE : WARN },
            ticks: { color: BG_ALT, maxRotation: 0, autoSkip: true, autoSkipPadding: 8 },
          },
        },
        elements: {
          bar: { borderWidth: 0 },
          line: { borderWidth: 2 },
          point: { hitRadius: 8, hoverRadius: 4 },
        },
        layout: { padding: { left: 6, right: 12, top: 6, bottom: 6 } },
      },
    });
  }

  private updateChart(): void {
    if (!this.chart) return;
    this.chart.data.labels = this.labels() as any;

    if (this.chartMode() === "pu") {
      const bar = this.dsPUBar();
      const line = this.dsPUAvg();
      if ((this.chart.data.datasets?.length ?? 0) >= 2) {
        const d0 = this.chart.data.datasets[0] as any;
        d0.data = bar.data;
        const d1 = this.chart.data.datasets[1] as any;
        d1.data = line.data;
      }
    } else {
      const ca = this.dsCA();
      const cost = this.dsCost();
      const margin = this.dsMargin();
      if ((this.chart.data.datasets?.length ?? 0) >= 3) {
        const d0 = this.chart.data.datasets[0] as any;
        d0.data = ca.data;
        const d1 = this.chart.data.datasets[1] as any;
        d1.data = cost.data;
        const d2 = this.chart.data.datasets[2] as any;
        d2.data = margin.data;
      }
    }
    this.chart.update("none");
  }

  private destroyChart(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }
  }

  protected readonly Math = Math;
}
